/**
 * Firebase Authentication Service - TypeScript Migration
 */

import firebase from 'firebase/app';
import 'firebase/auth';
import { createUserDocument } from './user';
import { SignupData, LoginData } from '@/types/user.types';

/**
 * Creates a new user account with email and password
 * @param signupData - User registration data
 * @returns Firebase user object
 * @throws FirebaseError if signup fails
 */
export const signup = async (signupData: SignupData): Promise<firebase.User> => {
  const { firstName, lastName, receiveEmail, email, password, phone } = signupData;
  
  try {
    const userCredential = await firebase
      .auth()
      .createUserWithEmailAndPassword(email, password);
    
    const user = userCredential.user;
    
    if (!user) {
      throw new Error('User creation failed - no user returned');
    }

    // Update profile with display name and store metadata in photoURL
    await user.updateProfile({
      displayName: `${firstName} ${lastName}`,
      photoURL: `${receiveEmail}|${phone}`
    });

    // Create user document in Firestore
    await createUserDocument(user);
    
    return user;
  } catch (error) {
    console.error('Signup error:', error);
    throw error;
  }
};

/**
 * Signs out the current user
 * @returns Promise that resolves when signout is complete
 */
export const logout = async (): Promise<void> => {
  try {
    await firebase.auth().signOut();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Signs in user with email and password
 * @param loginData - User login credentials
 * @returns Firebase user object
 * @throws FirebaseError if login fails
 */
export const login = async (loginData: LoginData): Promise<firebase.User> => {
  const { email, password } = loginData;
  
  try {
    const userCredential = await firebase
      .auth()
      .signInWithEmailAndPassword(email, password);
    
    if (!userCredential.user) {
      throw new Error('Login failed - no user returned');
    }

    return userCredential.user;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Updates the current user's password
 * @param newPassword - New password to set
 * @throws FirebaseError if update fails or user not authenticated
 */
export const updatePassword = async (newPassword: string): Promise<void> => {
  const user = firebase.auth().currentUser;
  
  if (!user) {
    throw new Error('No authenticated user found');
  }

  try {
    await user.updatePassword(newPassword);
    console.log('Password successfully updated');
    alert('Password successfully updated');
  } catch (error) {
    console.error('Error during password update:', error);
    alert(`Error during password update: ${error}`);
    throw error;
  }
};

/**
 * Sends password reset email to user
 * @param email - Email address to send reset link to
 * @param continueUrl - URL to redirect to after password reset
 * @throws FirebaseError if email send fails
 */
export const sendResetEmail = async (
  email: string,
  continueUrl: string
): Promise<void> => {
  const actionCodeSettings: firebase.auth.ActionCodeSettings = {
    url: continueUrl
  };

  try {
    await firebase.auth().sendPasswordResetEmail(email, actionCodeSettings);
    console.log('Sent email with link to reset password');
    alert('Sent email with link to reset password');
  } catch (error) {
    console.error('Error sending reset email:', error);
    alert('Error resetting password');
    throw error;
  }
};

/**
 * Gets the current authenticated user
 * @returns Current user or null if not authenticated
 */
export const getCurrentUser = (): firebase.User | null => {
  return firebase.auth().currentUser;
};

/**
 * Reauthenticates user with their password (required before sensitive operations)
 * @param password - User's current password
 * @throws FirebaseError if reauthentication fails
 */
export const reauthenticateUser = async (password: string): Promise<void> => {
  const user = firebase.auth().currentUser;
  
  if (!user || !user.email) {
    throw new Error('No authenticated user found');
  }

  const credential = firebase.auth.EmailAuthProvider.credential(
    user.email,
    password
  );

  try {
    await user.reauthenticateWithCredential(credential);
  } catch (error) {
    console.error('Reauthentication error:', error);
    throw error;
  }
};

/**
 * Checks if user email is verified
 * @returns True if email is verified
 */
export const isEmailVerified = (): boolean => {
  const user = firebase.auth().currentUser;
  return user?.emailVerified ?? false;
};

/**
 * Sends email verification to current user
 * @throws FirebaseError if send fails
 */
export const sendEmailVerification = async (): Promise<void> => {
  const user = firebase.auth().currentUser;
  
  if (!user) {
    throw new Error('No authenticated user found');
  }

  try {
    await user.sendEmailVerification();
    alert('Verification email sent');
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};